import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";

/** Server-minted stable block key (uuid). Client ids for new blocks are uuids too, which is fine: the server keeps a valid key it is handed. */
const isBlockKey = (id: string | undefined): id is string =>
  typeof id === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
import type { RootState } from "@/app/types";
import { createCourse } from "@/api/createCourse";
import { getCourseDetail } from "@/api/getCourseDetail";
import { updateCourse as updateCourseApi } from "@/api/updateCourse/updateCourseApi";
import { importCourseDocument } from "@/api/importCourseDocument/importCourseDocumentApi";
import type { ImportCourseDocumentResponse } from "@/api/importCourseDocument/types";
import type { CreateCoursePayload } from "@/api/createCourse";
import type { UpdateCoursePayload } from "@/api/updateCourse/types";
import type { CourseWithDetails } from "@/modules/learningCenter/types/course";
import type {
  Module,
  CourseFormData,
  CourseDetail,
} from "@/types/learningCenter";

/**
 * Course Editor State
 *
 * Manages the state for the enhanced course creation workflow
 */
export interface CourseEditorState {
  // Workflow State
  createdCourseId: string | null; // null = stage 1, value = stage 2
  activeTab: string; // 'details' | moduleId

  // Course Data
  courseDetails: CourseFormData | null;
  modules: Module[];

  // UI State
  isAddModuleDialogOpen: boolean;
  isDeleteModuleDialogOpen: boolean;
  moduleToDelete: string | null;

  // Form State for dialogs
  newModuleTitle: string;
  newModuleDescription: string;

  // Loading/Error
  loading: {
    createCourse: boolean;
    createModules: boolean;
    loadCourse: boolean;
    updateCourse: boolean;
    importDocument: boolean;
  };
  error: {
    createCourse: string | null;
    createModules: string | null;
    loadCourse: string | null;
    updateCourse: string | null;
    importDocument: string | null;
  };
}

// Initial state
const initialState: CourseEditorState = {
  createdCourseId: null,
  activeTab: "details",
  courseDetails: null,
  modules: [],
  isAddModuleDialogOpen: false,
  isDeleteModuleDialogOpen: false,
  moduleToDelete: null,
  newModuleTitle: "",
  newModuleDescription: "",
  loading: {
    createCourse: false,
    createModules: false,
    loadCourse: false,
    updateCourse: false,
    importDocument: false,
  },
  error: {
    createCourse: null,
    createModules: null,
    loadCourse: null,
    updateCourse: null,
    importDocument: null,
  },
};

/**
 * Async thunk: Load course for editing
 */
export const loadCourseForEdit = createAsyncThunk<
  CourseDetail,
  number,
  { rejectValue: string }
>("courseEditor/loadCourseForEdit", async (courseId, { rejectWithValue }) => {
  try {
    const response = await getCourseDetail(courseId);
    return response;
  } catch (error) {
    const message =
      error instanceof Error && "response" in error
        ? (error as { response?: { data?: { message?: string } } }).response
            ?.data?.message
        : undefined;
    return rejectWithValue(message || "Failed to load course");
  }
});

/**
 * Async thunk: Create course with details and modules
 */
export const createCourseWithModules = createAsyncThunk<
  CourseWithDetails,
  { courseDetails: CourseFormData; modules: Module[] },
  { rejectValue: string }
>(
  "courseEditor/createCourseWithModules",
  async ({ courseDetails, modules }, { rejectWithValue }) => {
    try {
      // Transform modules to API format
      const apiModules = modules.map((module) => ({
        title: module.title,
        description: module.description,
        order_number: module.order_number,
        // Library provenance forwarded for the backend to honor (FE snapshot for now)
        library_module_id: module.library_module_id,
        link_type: module.link_type,
        contentBlocks: module.contentBlocks.map((block) => ({
          type: block.type,
          content: block.content,
          order_number: block.order_number,
          fileUuid: block.fileUuid,
          title: block.title,
          shortDescription: block.shortDescription,
          blockKey: isBlockKey(block.id) ? block.id : undefined,
        })),
      }));

      // Create payload matching API expectations
      const payload: CreateCoursePayload = {
        title: courseDetails.title,
        tagline: courseDetails.tagline,
        description: courseDetails.description,
        difficulty_level: courseDetails.difficulty_level,
        estimated_hours: courseDetails.estimated_hours,
        thumbnail_path: courseDetails.thumbnail_url,
        hero_banner_path: courseDetails.hero_banner_url,
        skillIds: courseDetails.skillIds,
        categoryIds: courseDetails.categoryIds,
        modules: apiModules,
        target_audience: courseDetails.target_audience,
        industry: courseDetails.industry,
        level: courseDetails.level,
        estimated_duration: courseDetails.estimated_duration,
        learning_objectives: courseDetails.learning_objectives,
        course_overview: courseDetails.course_overview,
      };

      const response = await createCourse(payload);
      // createCourse already returns response.data from axios, so response IS the data
      return response;
    } catch (error) {
      const message =
        error instanceof Error && "response" in error
          ? (error as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : undefined;
      return rejectWithValue(message || "Failed to create course");
    }
  },
);

/**
 * Async thunk: Update existing course with details and modules
 */
export const updateCourseWithModules = createAsyncThunk<
  CourseWithDetails,
  { courseId: number; courseDetails: CourseFormData; modules: Module[] },
  { rejectValue: string }
>(
  "courseEditor/updateCourseWithModules",
  async ({ courseId, courseDetails, modules }, { rejectWithValue }) => {
    try {
      // Transform modules to API format. The edit endpoint replaces all modules
      // with what we send, so include the full module list (with content blocks)
      // — otherwise module edits made in the editor are silently dropped.
      const apiModules = modules.map((module) => ({
        title: module.title,
        description: module.description,
        order_number: module.order_number,
        library_module_id: module.library_module_id,
        link_type: module.link_type,
        contentBlocks: module.contentBlocks.map((block) => ({
          type: block.type,
          content: block.content,
          order_number: block.order_number,
          fileUuid: block.fileUuid,
          title: block.title,
          shortDescription: block.shortDescription,
          blockKey: isBlockKey(block.id) ? block.id : undefined,
        })),
      }));

      const payload: UpdateCoursePayload = {
        title: courseDetails.title,
        tagline: courseDetails.tagline,
        description: courseDetails.description,
        difficulty_level: courseDetails.difficulty_level,
        estimated_hours: courseDetails.estimated_hours,
        thumbnail_path: courseDetails.thumbnail_url,
        hero_banner_path: courseDetails.hero_banner_url,
        skillIds: courseDetails.skillIds,
        categoryIds: courseDetails.categoryIds,
        modules: apiModules,
        target_audience: courseDetails.target_audience,
        industry: courseDetails.industry,
        level: courseDetails.level,
        estimated_duration: courseDetails.estimated_duration,
        learning_objectives: courseDetails.learning_objectives,
        course_overview: courseDetails.course_overview,
      };

      console.log("[updateCourse] Sending PUT to course", courseId, payload);
      const response = await updateCourseApi(courseId, payload);
      console.log("[updateCourse] Raw API response:", response);
      // updateCourseApi returns response.data from Axios.
      // Backend may return { data: CourseWithDetails } or CourseWithDetails directly.
      const course =
        (response as any)?.data && (response as any).data.id
          ? (response as any).data
          : response;
      console.log("[updateCourse] Extracted course:", course);
      return course as unknown as CourseWithDetails;
    } catch (error) {
      const message =
        error instanceof Error && "response" in error
          ? (error as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : undefined;
      return rejectWithValue(message || "Failed to update course");
    }
  },
);

/**
 * Async thunk: Import course from Word document
 */
export const importCourseFromDocument = createAsyncThunk<
  ImportCourseDocumentResponse,
  File,
  { rejectValue: string }
>(
  "courseEditor/importCourseFromDocument",
  async (file, { rejectWithValue }) => {
    try {
      const response = await importCourseDocument(file);
      if (!response.success || !response.data) {
        return rejectWithValue(response.error || "Failed to import document");
      }
      return response;
    } catch (error) {
      const message =
        error instanceof Error && "response" in error
          ? (error as { response?: { data?: { error?: string } } }).response
              ?.data?.error
          : undefined;
      return rejectWithValue(message || "Failed to import document");
    }
  },
);

/**
 * Course Editor Slice
 */
const courseEditorSlice = createSlice({
  name: "courseEditor",
  initialState,
  reducers: {
    // Workflow state
    setCreatedCourseId: (state, action: PayloadAction<string | null>) => {
      state.createdCourseId = action.payload;
    },
    setActiveTab: (state, action: PayloadAction<string>) => {
      state.activeTab = action.payload;
    },

    // Course details
    setCourseDetails: (state, action: PayloadAction<CourseFormData>) => {
      state.courseDetails = action.payload;
    },

    // Modules management
    setModules: (state, action: PayloadAction<Module[]>) => {
      state.modules = action.payload;
    },
    addModule: (state, action: PayloadAction<Module>) => {
      state.modules.push(action.payload);
    },
    updateModule: (
      state,
      action: PayloadAction<{ id: string; module: Partial<Module> }>,
    ) => {
      const index = state.modules.findIndex((m) => m.id === action.payload.id);
      if (index !== -1) {
        state.modules[index] = {
          ...state.modules[index],
          ...action.payload.module,
        };
      }
    },
    deleteModule: (state, action: PayloadAction<string>) => {
      state.modules = state.modules.filter((m) => m.id !== action.payload);
      // Recalculate order numbers
      state.modules.forEach((module, index) => {
        module.order_number = index;
      });
    },
    reorderModules: (state, action: PayloadAction<Module[]>) => {
      // Update modules with new order
      state.modules = action.payload.map((module, index) => ({
        ...module,
        order_number: index,
      }));
    },

    // Content block management
    addContentBlock: (
      state,
      action: PayloadAction<{
        moduleId: string;
        contentBlock: Module["contentBlocks"][0];
      }>,
    ) => {
      const module = state.modules.find(
        (m) => m.id === action.payload.moduleId,
      );
      if (module) {
        module.contentBlocks.push(action.payload.contentBlock);
      }
    },
    updateContentBlock: (
      state,
      action: PayloadAction<{
        moduleId: string;
        blockId: string;
        updates: Partial<Module["contentBlocks"][0]>;
      }>,
    ) => {
      const module = state.modules.find(
        (m) => m.id === action.payload.moduleId,
      );
      if (module) {
        const blockIndex = module.contentBlocks.findIndex(
          (b) => b.id === action.payload.blockId,
        );
        if (blockIndex !== -1) {
          module.contentBlocks[blockIndex] = {
            ...module.contentBlocks[blockIndex],
            ...action.payload.updates,
          };
        }
      }
    },
    removeContentBlock: (
      state,
      action: PayloadAction<{ moduleId: string; blockId: string }>,
    ) => {
      const module = state.modules.find(
        (m) => m.id === action.payload.moduleId,
      );
      if (module) {
        module.contentBlocks = module.contentBlocks.filter(
          (b) => b.id !== action.payload.blockId,
        );
        // Recalculate order numbers
        module.contentBlocks.forEach((block, index) => {
          block.order_number = index;
        });
      }
    },
    reorderContentBlocks: (
      state,
      action: PayloadAction<{
        moduleId: string;
        contentBlocks: Module["contentBlocks"];
      }>,
    ) => {
      const module = state.modules.find(
        (m) => m.id === action.payload.moduleId,
      );
      if (module) {
        // Update content blocks with new order
        module.contentBlocks = action.payload.contentBlocks.map(
          (block, index) => ({
            ...block,
            order_number: index,
          }),
        );
      }
    },

    // Dialog state
    setAddModuleDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isAddModuleDialogOpen = action.payload;
    },
    setDeleteModuleDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isDeleteModuleDialogOpen = action.payload;
    },
    setModuleToDelete: (state, action: PayloadAction<string | null>) => {
      state.moduleToDelete = action.payload;
    },

    // Dialog form state
    setNewModuleTitle: (state, action: PayloadAction<string>) => {
      state.newModuleTitle = action.payload;
    },
    setNewModuleDescription: (state, action: PayloadAction<string>) => {
      state.newModuleDescription = action.payload;
    },

    // Reset state
    resetEditor: () => initialState,

    // Clear errors
    clearErrors: (state) => {
      state.error.createCourse = null;
      state.error.createModules = null;
      state.error.loadCourse = null;
      state.error.updateCourse = null;
      state.error.importDocument = null;
    },

    // Clear stale loading states (e.g., from a previous failed session)
    clearLoading: (state) => {
      state.loading.createCourse = false;
      state.loading.createModules = false;
      state.loading.updateCourse = false;
      state.loading.importDocument = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Load course for editing
      .addCase(loadCourseForEdit.pending, (state) => {
        state.loading.loadCourse = true;
        state.error.loadCourse = null;
      })
      .addCase(loadCourseForEdit.fulfilled, (state, action) => {
        state.loading.loadCourse = false;
        const course = action.payload;

        // Set the created course ID to switch to stage 2
        state.createdCourseId = course.id.toString();

        // Populate course details
        state.courseDetails = {
          title: course.title,
          tagline: course.tagline || undefined,
          description: course.description,
          difficulty_level: course.difficulty_level,
          estimated_hours: parseFloat(course.estimated_hours),
          thumbnail_url: course.thumbnail_path || undefined,
          hero_banner_url: course.hero_banner_path || undefined,
          skillIds: course.skills?.map((s) => s.id) || [],
          categoryIds: course.categories?.map((c) => c.id) || [],
          target_audience: (course as any).target_audience || undefined,
          industry: (course as any).industry || undefined,
          level: (course as any).level || undefined,
          estimated_duration: (course as any).estimated_duration || undefined,
          learning_objectives: (course as any).learning_objectives || [],
          course_overview: (course as any).course_overview || undefined,
        };

        // Transform CourseModule to Module format for editing.
        // getCourseDetail returns each module's content blocks as
        // `contentBlocks`, so carry them into editor state — otherwise the
        // editor shows blank modules and a save could drop existing content.
        state.modules =
          course.modules?.map((module, idx) => ({
            id: module.id,
            title: module.title,
            description: module.description,
            order_number: idx,
            // A block's stable key doubles as its editor id so it survives
            // every round trip and goes back to the server on save.
            contentBlocks: (module.contentBlocks ?? []).map((block) => ({
              ...block,
              id: block.blockKey ?? block.id,
            })),
          })) || [];
      })
      .addCase(loadCourseForEdit.rejected, (state, action) => {
        state.loading.loadCourse = false;
        state.error.loadCourse = action.payload ?? "Failed to load course";
      })
      // Create course with modules
      .addCase(createCourseWithModules.pending, (state) => {
        state.loading.createCourse = true;
        state.error.createCourse = null;
      })
      .addCase(createCourseWithModules.fulfilled, (state, action) => {
        state.loading.createCourse = false;
        state.createdCourseId = action.payload.id.toString();
      })
      .addCase(createCourseWithModules.rejected, (state, action) => {
        state.loading.createCourse = false;
        state.error.createCourse = action.payload ?? "Failed to create course";
      })
      // Update course with modules
      .addCase(updateCourseWithModules.pending, (state) => {
        state.loading.updateCourse = true;
        state.error.updateCourse = null;
      })
      .addCase(updateCourseWithModules.fulfilled, (state, action) => {
        state.loading.updateCourse = false;
        // Update course details in state if payload is valid
        const course = action.payload;
        if (course && course.title) {
          state.courseDetails = {
            title: course.title,
            tagline: course.tagline || undefined,
            description: course.description,
            difficulty_level: course.difficulty_level,
            estimated_hours: parseFloat(course.estimated_hours || "0"),
            thumbnail_url: course.thumbnail_path || undefined,
            hero_banner_url: course.hero_banner_path || undefined,
            skillIds: course.skills?.map((s) => s.id) || [],
            categoryIds: course.categories?.map((c) => c.id) || [],
          };
        }
      })
      .addCase(updateCourseWithModules.rejected, (state, action) => {
        state.loading.updateCourse = false;
        state.error.updateCourse = action.payload ?? "Failed to update course";
      })
      // Import course from document
      .addCase(importCourseFromDocument.pending, (state) => {
        state.loading.importDocument = true;
        state.error.importDocument = null;
      })
      .addCase(importCourseFromDocument.fulfilled, (state, action) => {
        state.loading.importDocument = false;
        const { courseDetails, modules } = action.payload.data!;

        // Populate course details (user sets skillIds/categoryIds manually)
        state.courseDetails = {
          title: courseDetails.title,
          description: courseDetails.description,
          difficulty_level: courseDetails.difficulty_level,
          estimated_hours: courseDetails.estimated_hours,
          tagline: courseDetails.tagline,
          target_audience: courseDetails.target_audience,
          industry: courseDetails.industry,
          level: courseDetails.level,
          estimated_duration: courseDetails.estimated_duration,
          learning_objectives: courseDetails.learning_objectives,
          course_overview: courseDetails.course_overview,
          skillIds: [],
          categoryIds: [],
        };

        // Populate modules with client-generated UUIDs
        state.modules = modules.map((module) => ({
          id: crypto.randomUUID(),
          title: module.title,
          description: module.description,
          order_number: module.order_number,
          contentBlocks: module.contentBlocks.map((block) => ({
            id: crypto.randomUUID(),
            type: block.type,
            content: block.content,
            order_number: block.order_number,
          })),
        }));

        // Transition to Stage 2
        state.createdCourseId = "draft";
        state.activeTab = "details";
      })
      .addCase(importCourseFromDocument.rejected, (state, action) => {
        state.loading.importDocument = false;
        state.error.importDocument =
          action.payload ?? "Failed to import document";
      });
  },
});

// Actions
export const {
  setCreatedCourseId,
  setActiveTab,
  setCourseDetails,
  setModules,
  addModule,
  updateModule,
  deleteModule,
  reorderModules,
  addContentBlock,
  updateContentBlock,
  removeContentBlock,
  reorderContentBlocks,
  setAddModuleDialogOpen,
  setDeleteModuleDialogOpen,
  setModuleToDelete,
  setNewModuleTitle,
  setNewModuleDescription,
  resetEditor,
  clearErrors,
  clearLoading,
} = courseEditorSlice.actions;

// Selectors
export const selectCreatedCourseId = (state: RootState) =>
  state.courseEditor?.createdCourseId;
export const selectActiveTab = (state: RootState) =>
  state.courseEditor?.activeTab;
export const selectCourseDetails = (state: RootState) =>
  state.courseEditor?.courseDetails;
export const selectModules = (state: RootState) =>
  state.courseEditor?.modules || [];
export const selectIsAddModuleDialogOpen = (state: RootState) =>
  state.courseEditor?.isAddModuleDialogOpen;
export const selectIsDeleteModuleDialogOpen = (state: RootState) =>
  state.courseEditor?.isDeleteModuleDialogOpen;
export const selectModuleToDelete = (state: RootState) =>
  state.courseEditor?.moduleToDelete;
export const selectNewModuleTitle = (state: RootState) =>
  state.courseEditor?.newModuleTitle;
export const selectNewModuleDescription = (state: RootState) =>
  state.courseEditor?.newModuleDescription;
export const selectCourseEditorLoading = (state: RootState) =>
  state.courseEditor?.loading;
export const selectCourseEditorErrors = (state: RootState) =>
  state.courseEditor?.error;

// Reducer
export default courseEditorSlice.reducer;
